// src/components/InventoryManagement/List/InventoryTable.js
import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MaterialReactTable } from 'material-react-table';
import {
    IconButton,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import FilterInventory from '../Popup/filterInventory';
import AddInventoryPopup from '../Popup/AddInventory';
import UpdateInventoryPopup from '../Popup/UpdateInventoryPopup';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import Navigationmenu from '../NavigationMenu/navigationMenu';
import DoctorManagementMenu from "../../../components/common/Menu/DoctorManagement";
import TopHeader from "../../../components/common/Header/TopHeader";
import { API_BASE_URL, API_INVENTORY } from '../../../helpers/urlHelper';

export default function InventoryTable() {
    // Estados principales
    const [inventoryData, setInventoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [rowSelection, setRowSelection] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Popups y menús
    const [anchorEl, setAnchorEl] = useState(null);
    const [openFilter, setOpenFilter] = useState(false);
    const [openPopup, setOpenPopup] = useState(false);
    const [openUpdatePopup, setOpenUpdatePopup] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [openInfo, setOpenInfo] = useState(false);
    const [infoData, setInfoData] = useState(null);

    // Estados de filtros
    const [activeFilters, setActiveFilters] = useState({});
    const [showKPIs, setShowKPIs] = useState(true);
    const [showActiveFilters, setShowActiveFilters] = useState(true);

    // Fetch inicial
    useEffect(() => {
        fetchInventory();
    }, []);

    const fetchInventory = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}${API_INVENTORY}`);
            const data = await response.json();
            const arr = data.map((item, idx) => {
                const qty = item.stock;
                const lastUpdatedDate = item.updatedAt ? new Date(item.updatedAt) : null;
                const expiryDateDate = item.specifications?.expiryDate
                    ? new Date(item.specifications.expiryDate)
                    : null;
                const action =
                    qty < 50
                        ? 'Need to add new stock immediately'
                        : qty < 150
                            ? 'Running out of stock soon'
                            : 'Stock Available';
                return {
                    _id: item._id,
                    inventoryId: idx + 1,
                    centerId: 'CENTER001',
                    medicineId: item.id,
                    medicineName: item.name,
                    quantity: qty,
                    stock: qty,
                    price: item.price,
                    laboratory: item.specifications?.laboratory || '',
                    code: item.code,
                    description: item.description,
                    uom: item.uom,
                    dosage: item.specifications?.dosage || '',
                    presentation: item.specifications?.presentation || '',
                    activeIngredient: item.specifications?.activeIngredient || '',
                    action,
                    lastUpdated: lastUpdatedDate?.toLocaleDateString() || '',
                    expiryDate: expiryDateDate?.toLocaleDateString() || '',
                    lastUpdatedDate,
                    expiryDateDate,
                };
            });
            setInventoryData(arr);
            setFilteredData(arr);
        } catch (error) {
            console.error('Error fetching inventory:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Aplicar filtros
    const applyFilters = (filters) => {
        const now = Date.now();
        let f = [...inventoryData];

        if (filters.healthCenter) {
            f = f.filter((i) => i.centerId === filters.healthCenter);
        }
        if (filters.medicineCategory) {
            f = f.filter((i) =>
                i.medicineName.toLowerCase().includes(filters.medicineCategory.toLowerCase())
            );
        }
        if (filters.stockStatus && filters.stockStatus !== 'all') {
            f = f.filter((i) => {
                const q = i.quantity;
                if (filters.stockStatus === 'inStock') return q > 50;
                if (filters.stockStatus === 'lowStock') return q > 0 && q <= 150;
                if (filters.stockStatus === 'outOfStock') return q === 0;
                return true;
            });
        }
        if (filters.expiryDate && filters.expiryDate !== 'all') {
            f = f.filter((i) => {
                const exp = i.expiryDateDate;
                if (!(exp instanceof Date)) return false;
                const diff = exp.getTime() - now;
                if (filters.expiryDate === '30days') return diff >= 0 && diff <= 30*24*60*60*1000;
                if (filters.expiryDate === '60days') return diff >= 0 && diff <= 60*24*60*60*1000;
                if (filters.expiryDate === '90days') return diff >= 0 && diff <= 90*24*60*60*1000;
                if (filters.expiryDate === 'expired') return diff < 0;
                return true;
            });
        }
        if (filters.supplier) {
            f = f.filter((i) => i.laboratory === filters.supplier);
        }
        if (filters.lastUpdated && filters.lastUpdated !== 'all') {
            f = f.filter((i) => {
                const upd = i.lastUpdatedDate;
                if (!(upd instanceof Date)) return false;
                const elapsed = now - upd.getTime();
                if (filters.lastUpdated === 'today')
                    return upd.toDateString() === new Date(now).toDateString();
                if (filters.lastUpdated === 'lastWeek')
                    return elapsed <= 7*24*60*60*1000;
                if (filters.lastUpdated === 'lastMonth')
                    return elapsed <= 30*24*60*60*1000;
                if (filters.lastUpdated === 'customDate' && filters.customDateValue) {
                    const cd = new Date(filters.customDateValue);
                    return upd.toDateString() === cd.toDateString();
                }
                return true;
            });
        }
        const minP = Number(filters.priceMin) || 0;
        const maxP = Number(filters.priceMax) || Infinity;
        f = f.filter((i) => {
            const p = Number(i.price) || 0;
            return p >= minP && p <= maxP;
        });
        if (filters.quantity) {
            const qn = Number(filters.quantity);
            f = f.filter((i) => i.quantity === qn);
        }

        setFilteredData(f);
        setActiveFilters(filters);
        setOpenFilter(false);
    };

    // Filtrado con búsqueda
    const finalFilteredData = useMemo(() => {
        return filteredData.filter(item => {
            const matchesSearchTerm = searchTerm
                ? Object.values(item).some(value =>
                    value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
                )
                : true;
            return matchesSearchTerm;
        });
    }, [filteredData, searchTerm]);

    // Handlers
    const handleOpenInfo = (row) => {
        setInfoData(row);
        setOpenInfo(true);
    };

    const handleCloseInfo = () => {
        setOpenInfo(false);
    };

    const handleOpenFilter = () => setOpenFilter(true);
    const handleCloseFilter = () => setOpenFilter(false);
    const handleOpenPopup = () => setOpenPopup(true);
    const handleClosePopup = () => {
        setOpenPopup(false);
        fetchInventory();
    };

    const handleActionsClick = (event) => setAnchorEl(event.currentTarget);
    const handleActionsClose = () => setAnchorEl(null);

    const handleUpdateSelected = () => {
        handleActionsClose();
        const ids = Object.keys(rowSelection).filter((id) => rowSelection[id]);
        if (ids.length !== 1) {
            alert('Debe seleccionar exactamente un registro para actualizar');
            return;
        }
        const sel = ids[0];
        const itm = filteredData.find((r) => r._id === sel) || inventoryData.find((r) => r._id === sel);
        if (itm) {
            setSelectedRowData(itm);
            setOpenUpdatePopup(true);
        }
    };

    const handleToPdf = () => {
        handleActionsClose();
        const ids = Object.keys(rowSelection).filter((id) => rowSelection[id]);
        if (!ids.length) {
            alert('No hay elementos seleccionados para exportar.');
            return;
        }
        const rows = filteredData
            .filter((i) => ids.includes(i._id))
            .map((i) => [
                i.inventoryId,
                i.centerId,
                i.medicineId,
                i.medicineName,
                i.quantity,
                i.lastUpdated,
                i.action,
            ]);
        const doc = new jsPDF();
        autoTable(doc, {
            head: [['Inventory Id #', 'Center Id #', 'Medicine Id #', 'Medicine Name', 'Quantity #', 'Last Updated #', 'Action to Take #']],
            body: rows,
        });
        doc.save('selected-data.pdf');
    };

    const handleToExcel = () => {
        handleActionsClose();
        const ids = Object.keys(rowSelection).filter((id) => rowSelection[id]);
        if (!ids.length) {
            alert('No hay elementos seleccionados para exportar.');
            return;
        }
        const sheet = XLSX.utils.json_to_sheet(filteredData.filter((i) => ids.includes(i._id)));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, 'Selected Data');
        XLSX.writeFile(book, 'selected-data.xlsx');
    };

    const handleRemoveSelected = () => {
        handleActionsClose();
        const ids = Object.keys(rowSelection).filter((id) => rowSelection[id]);
        if (!ids.length) {
            alert('No hay elementos seleccionados para eliminar.');
            return;
        }
        if (!window.confirm('¿Eliminar los seleccionados?')) return;
        Promise.all(
            ids.map((id) =>
                fetch(`${API_BASE_URL}/model/product/${id}`, {
                    method: 'DELETE',
                })
            )
        ).then(() => {
            fetchInventory();
            setRowSelection({});
        });
    };

    const clearFilter = (key) => {
        const newFilters = { ...activeFilters };
        delete newFilters[key];
        setActiveFilters(newFilters);
        applyFilters(newFilters);
    };

    const handleClearAll = () => {
        setActiveFilters({});
        setFilteredData(inventoryData);
        setSearchTerm("");
    };

    // Columnas
    const columns = useMemo(
        () => [
            {
                accessorKey: 'inventoryId',
                header: 'Inventory Id #',
                Cell: ({ row }) => (
                    <div className="d-flex align-items-center gap-2">
                        <span>{row.original.inventoryId}</span>
                        <IconButton size="small" onClick={() => handleOpenInfo(row.original)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </div>
                ),
            },
            { accessorKey: 'centerId', header: 'Center Id #' },
            { accessorKey: 'medicineId', header: 'Medicine Id #' },
            { accessorKey: 'medicineName', header: 'Medicine Name' },
            { accessorKey: 'quantity', header: 'Quantity #' },
            { accessorKey: 'lastUpdated', header: 'Last Updated #' },
            {
                accessorKey: 'action',
                header: 'Action to Take #',
                Cell: ({ row }) => {
                    const txt = row.original.action;
                    const style = txt.includes('immediately')
                        ? { color: 'red', fontWeight: 'bold' }
                        : txt.includes('soon')
                            ? { color: 'orange', fontWeight: 'bold' }
                            : { color: 'green', fontWeight: 'bold' };
                    return <span style={style}>{txt}</span>;
                },
            },
        ],
        []
    );

    return (
        <main id="app">
            <TopHeader />

            <div className="content_wrapper">
                <section className="doctor-management-wrapper bg-white pb-5">
                    
                    {/* Inner Menu */}
                    <DoctorManagementMenu activeMenu='inventory' />

                    {/* Body Container */}
                    <div className="container-fluid px-lg-5">

                        {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Inventory</li>
                            </ol>
                        </nav>

                        {/* KPI Section */}
                        {showKPIs && (
                            <Navigationmenu 
                                inventoryData={inventoryData}
                                filteredInventoryData={finalFilteredData}
                            />
                        )}

                        {/* Action Buttons */}
                        <div className="filter-container py-3">
                            <div className="row align-items-center">

                                <div className="col-md-5 col-lg-6 d-none d-md-block mb-3 mb-md-0">
                                    <div className="form-group d-flex align-items-center flex-row-reverse position-relative">
                                        <input
                                            type="text"
                                            className="form-control ps-2 bg-transparent border-0 shadow-none"
                                            placeholder="Search inventory..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button type="submit" className="p-0 bg-transparent border-0">
                <span className="d-block material-symbols-outlined icon-lg">search</span>
            </button>
                                    </div>
                                </div>

                                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 gap-sm-2 gy-3 flex-wrap">
                                    
                                    {/* Action dropdown */}
                                    <div className="dropdown">
                                        <button 
                                            className="btn btn-gray dropdownbtn d-flex align-items-center gap-1"
                                            onClick={handleActionsClick}
                                        >
                                            <span className="d-block">Action </span>
                                            <span className="d-block material-symbols-outlined icon-sm">expand_more</span>
                                        </button>
                                    </div>

                                    {/* Filter button */}
                                    <Link 
                                        to="#" 
                                        className="btn btn-gray filterbtn d-flex align-items-center gap-1 position-relative" 
                                        onClick={handleOpenFilter}
                                    >
                                        <div className="filter_text d-flex align-items-center gap-1">
                                            <span className="d-block material-symbols-outlined icon-md">tune</span>
                                            <span className="d-block">Filter</span>
                                        </div>
                                        {Object.values(activeFilters).filter(v => v && v !== 'all').length > 0 && (
                                            <span 
                                                className="position-absolute top-0 start-100 translate-middle badge rounded-circle bg-primary" 
                                                style={{fontSize: '0.6rem', padding: '0.2rem 0.4rem'}}
                                            >
                                                {Object.values(activeFilters).filter(v => v && v !== 'all').length}
                                            </span>
                                        )}
                                    </Link>
                                    
                                    {/* Add New button */}
                                    <Link 
                                        to="#" 
                                        className="btn btn-primary addnewbtn d-flex align-items-center gap-1" 
                                        onClick={handleOpenPopup}
                                    >
                                        <span className="d-block material-symbols-outlined icon-md">add</span>
                                        <span className="d-block">Add New</span>
                                    </Link>

                                </div>
                            </div>
                        </div>

                        {/* Active Filters */}
                        {showActiveFilters && Object.values(activeFilters).some(v => v && v !== 'all') && (
                            <div className="active-filters-wrapper mb-3 d-flex justify-content-between align-items-center">
                                <div className="  d-flex align-items-center flex-wrap gap-2">
                                    <span className="d-block fw-semibold text-gray">Active Filters:</span>
                                    
                                    {activeFilters.healthCenter && (
                                        <Chip
                                            size="small"
                                            label={`Center: ${activeFilters.healthCenter}`}
                                            onDelete={() => clearFilter('healthCenter')}
                                        />
                                    )}
                                    {activeFilters.medicineCategory && (
                                        <Chip
                                            size="small"
                                            label={`Medicine: ${activeFilters.medicineCategory}`}
                                            onDelete={() => clearFilter('medicineCategory')}
                                        />
                                    )}
                                    {activeFilters.stockStatus && activeFilters.stockStatus !== 'all' && (
                                        <Chip
                                            size="small"
                                            label={`Stock: ${activeFilters.stockStatus}`}
                                            onDelete={() => clearFilter('stockStatus')}
                                        />
                                    )}
                                    {activeFilters.expiryDate && activeFilters.expiryDate !== 'all' && (
                                        <Chip
                                            size="small"
                                            label={`Expiry: ${activeFilters.expiryDate}`}
                                            onDelete={() => clearFilter('expiryDate')}
                                        />
                                    )}
                                    {activeFilters.supplier && (
                                        <Chip
                                            size="small"
                                            label={`Supplier: ${activeFilters.supplier}`}
                                            onDelete={() => clearFilter('supplier')}
                                        />
                                    )}
                                    {(activeFilters.priceMin || activeFilters.priceMax) && (
                                        <Chip
                                            size="small"
                                            label={`Price: ${activeFilters.priceMin || 0} – ${activeFilters.priceMax || '∞'}`}
                                            onDelete={() => {
                                                clearFilter('priceMin');
                                                clearFilter('priceMax');
                                            }}
                                        />
                                    )}
                                </div>
                                
                                <button 
                                    className="btn btn-link text-primary p-0" 
                                    onClick={handleClearAll}
                                >
                                    Clear All
                                </button>
                            </div>
                        )}

                        {/* Tabla */}
                        <div className="table-wrapper">
                            <MaterialReactTable
                                columns={columns}
                                data={finalFilteredData}
                                state={{ isLoading, rowSelection }}
                                onRowSelectionChange={setRowSelection}
                                enableRowSelection={true}
                                enableColumnFilters
                                enableColumnOrdering
                                enableSorting
                                enablePagination
                                getRowId={(row) => row._id}
                                muiTableBodyRowProps={({ row }) => {
                                    const q = row.original.quantity;
                                    const bg =
                                        q < 50
                                            ? 'rgba(255, 0, 0, 0.05)'
                                            : q < 150
                                                ? 'rgba(255, 165, 0, 0.05)'
                                                : 'rgba(0, 128, 0, 0.05)';
                                    return {
                                        sx: {
                                            backgroundColor: bg,
                                            '&:hover': { backgroundColor: bg.replace('0.05', '0.1') },
                                        },
                                    };
                                }}
                            />
                        </div>

                    </div>
                </section>
            </div>

            {/* Menú de acciones */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleActionsClose}
            >
                <MenuItem onClick={handleToPdf}>Export to PDF</MenuItem>
                <MenuItem onClick={handleToExcel}>Export to Excel</MenuItem>
                <MenuItem onClick={handleUpdateSelected}>Update Selected</MenuItem>
                <MenuItem onClick={handleRemoveSelected}>Remove Selected</MenuItem>
            </Menu>

            {/* Popups */}
            <FilterInventory
                open={openFilter}
                onClose={handleCloseFilter}
                onApply={applyFilters}
            />
            <AddInventoryPopup 
                open={openPopup} 
                handleClose={handleClosePopup} 
            />
            <UpdateInventoryPopup
                open={openUpdatePopup}
                handleClose={() => {
                    setOpenUpdatePopup(false);
                    fetchInventory();
                }}
                data={selectedRowData}
                onUpdate={(u) => {
                    fetchInventory();
                }}
            />

            {/* Diálogo de información */}
            <Dialog open={openInfo} onClose={handleCloseInfo} maxWidth="sm" fullWidth>
                <DialogTitle>Product Information</DialogTitle>
                <DialogContent>
                    {infoData && (
                        <div className="d-flex flex-column gap-2 mt-2">
                            <p><strong>Inventory Id:</strong> {infoData.inventoryId}</p>
                            <p><strong>Center Id:</strong> {infoData.centerId}</p>
                            <p><strong>Medicine Id:</strong> {infoData.medicineId}</p>
                            <p><strong>Medicine Name:</strong> {infoData.medicineName}</p>
                            <p><strong>Code:</strong> {infoData.code}</p>
                            <p><strong>Description:</strong> {infoData.description}</p>
                            <p><strong>Dosage:</strong> {infoData.dosage}</p>
                            <p><strong>Presentation:</strong> {infoData.presentation}</p>
                            <p><strong>UOM:</strong> {infoData.uom}</p>
                            <p><strong>Active Ingredient:</strong> {infoData.activeIngredient}</p>
                            <p><strong>Expiry Date:</strong> {infoData.expiryDate}</p>
                            <p><strong>Quantity:</strong> {infoData.quantity}</p>
                            <p><strong>Last Updated:</strong> {infoData.lastUpdated}</p>
                            <p><strong>Price:</strong> {infoData.price}</p>
                            <p><strong>Laboratory:</strong> {infoData.laboratory}</p>
                            <p><strong>Action:</strong> {infoData.action}</p>
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseInfo}>Close</Button>
                </DialogActions>
            </Dialog>
        </main>
    );
}